<template>
    <div v-if="!isSupplier" class="d-flex flex-wrap card-body" style="gap: 0.5rem;">
        <router-link
            class="btn btn-link"
            :to="{name: 'CreateProductCard'}"
        >
            <i class="icon-plus3 mr-2"></i>
            {{ $t("buttons.create") }}
        </router-link>
    </div>
    <Table
        :handle="tableHandle"
        :url="table.url"
        :columns="table.columns"
        :hasCheckbox="true"
        :allow-column-settings="true"
        v-model:orders="table.orders"
        @click:filters-button="$refs.ProductsFiltersModal.show()"
        @click:clear-filters="clearFilters"
        @update:selectedValues="selectedUuids = $event"
        has-filters
        :active-filters-count="activeFiltersCount"
    >
        <template #actionDropdownItems>
            <DropdownItem @click="connectProducts()">{{ $t('buttons.connect') }}</DropdownItem>
            <DropdownItem @click="disconnectProducts()">{{ $t('buttons.detach') }}</DropdownItem>
            <DropdownItem v-if="!isSupplier" @click="rewriteInternalProducts()">{{ $t('buttons.rewrite_internal_products') }}</DropdownItem>
            <DropdownItem v-if="!isSupplier" @click="editProducts()">{{ $t('buttons.edit') }}</DropdownItem>
            <DropdownItem @click="$refs.CopyProductsModal.show(selectedUuids)">{{ $t('labels.copy_products_content_to_integration') }}</DropdownItem>
            <DropdownItem v-if="!isSupplier" @click="copyProductsContentFromIntegration()">{{ $t('labels.copy_products_content_from_integration') }}</DropdownItem>
            <DropdownItem v-if="!isSupplier" @click="removeProductItems()">{{ $t('buttons.delete') }}</DropdownItem>
            <DropdownItem v-if="!isSupplier" @click="$refs.UpdateProductContentModal.show(selectedUuids)">{{ $t('updateProductContentModal.actionLabel') }}</DropdownItem>
            <DropdownItem v-if="!isSupplier" @click="$refs.TranslateProductsContentModal.show(selectedUuids)">{{ $t('labels.translate_content') }}</DropdownItem>
            <DropdownItem v-if="!isSupplier" @click="generateProductsContent()">{{ $t('runAiProcessModal.showButton') }}</DropdownItem>
            <DropdownItem v-if="!isSupplier" @click="$refs.GroupProductModal.show(selectedUuids)">{{ $t('groupProductModal.showModalButton') }}</DropdownItem>
        </template>
        <template #td-id="{row}">
            <router-link :to="{name: 'Product', params: { integrationProductUuid: row.uuid }}" class="long-string">
                {{ row.id }}
            </router-link>
        </template>
        <template #td-product_item_id="{row}">
            <AppNullableCell>
                <template #default v-if="row.product">
                    <router-link :to="{name: 'EditProductPage', params: { productUuid: row.product.uuid }}" class="long-string">
                        {{ row.product.id }}
                    </router-link>
                </template>
            </AppNullableCell>
        </template>
        <template #td-source_id="{row}">
            <AppNullableCell>
                <template #default v-if="row.source_id">
                    <span class="long-string">{{ row.source_id }}</span>
                </template>
            </AppNullableCell>
        </template>
        <template #td-second_source_id="{row}">
            <AppNullableCell>
                <template #default v-if="row.second_source_id">
                    <span class="long-string">{{ row.second_source_id }}</span>
                </template>
            </AppNullableCell>
        </template>
        <template #td-group_key="{row}">
            <AppNullableCell>
                <template #default v-if="row.group_key">
                    <span class="long-string" :title="row.group_key">{{ row.group_key }}</span>
                </template>
            </AppNullableCell>
        </template>
        <template #td-image="{row}">
            <ImageZoomComponent
                :url="row.picture_url ?? PLACEHOLDER_IMAGE_URL"
                :alt="row.name"
                :default-width="50"
                :default-height="50"
            />
        </template>
        <template #td-integration_category_id="{row}">
            <AppNullableCell>
                <template #default v-if="row.category">
                    <router-link :to="{name: 'EditCategoryCard', params: { categoryUuid: row.category.uuid }}" class="long-string">
                        {{ getAvailableTransitions(row.category.names) }}
                    </router-link>
                </template>
            </AppNullableCell>
        </template>
        <template #[`td-name_${lang}`]="{row}" v-for="lang in integration.language_aliases" :key="`name_${lang}`">
            <AppNullableCell>
                <template #default v-if="row.names[lang]">
                    <span class="long-string" :title="row.names[lang]">{{ row.names[lang] }}</span>
                </template>
            </AppNullableCell>
        </template>
        <template #[`td-description_${lang}`]="{row}" v-for="lang in integration.language_aliases" :key="`description_${lang}`">
            <AppNullableCell>
                <template #default v-if="row.descriptions[lang]">
                    <span class="long-string" :title="row.descriptions[lang]">{{ row.descriptions[lang] }}</span>
                </template>
            </AppNullableCell>
        </template>
        <template #[`td-seo_${lang}`]="{row}" v-for="lang in integration.language_aliases" :key="`seo_${lang}`">
            <AppNullableCell>
                <template #default v-if="row.seo[lang]">
                    <span class="long-string" :title="row.seo[lang]">{{ row.seo[lang] }}</span>
                </template>
            </AppNullableCell>
        </template>
        <template #td-price="{row}">
            <AppNullableCell>
                <template #default v-if="row.price !== null">
                    <span>{{ row.price }}</span>
                </template>
            </AppNullableCell>
        </template>
        <template #td-sale_price="{row}">
            <AppNullableCell>
                <template #default v-if="row.sale_price !== null">
                    <span>{{ Price.factory(row.sale_price).toCurrencyString('UAH') }}</span>
                </template>
            </AppNullableCell>
        </template>
        <template #td-promo_price="{row}">
            <AppNullableCell>
                <template #default v-if="row.promo_price !== null">
                    <span>{{ Price.factory(row.promo_price).toCurrencyString('UAH') }}</span>
                </template>
            </AppNullableCell>
        </template>
        <template #td-balance="{row}">
            <AppNullableCell>
                <template #default v-if="row.balance !== null">
                    <span>{{ row.balance }}</span>
                </template>
            </AppNullableCell>
        </template>
        <template #td-export_enabled="{row}">
            <span>{{ row.export_enabled ? $t('labels.yes') : $t('labels.no') }}</span>
        </template>
        <template #td-manufacturer_name="{row}">
            <AppNullableCell>
                <template #default v-if="row.manufacturer_name">
                    <span class="long-string" :title="row.manufacturer_name">{{ row.manufacturer_name }}</span>
                </template>
            </AppNullableCell>
        </template>
        <template #td-measurement="{row}">
            <AppNullableCell>
                <template #default v-if="row.measurement">
                    <span>{{ row.measurement }}</span>
                </template>
            </AppNullableCell>
        </template>
        <template #td-created_at="{row}">
            <span>{{ DateFormatter.formatDateTime(row.created_at) }}</span>
        </template>
        <template #td-updated_at="{row}">
            <span>{{ DateFormatter.formatDateTime(row.updated_at) }}</span>
        </template>
        <template #td-tags="{row}">
            <AppNullableCell>
                <template #default v-if="row.tags && row.tags.length > 0">
                    <span class="long-string" :title="formatTagsString(row.tags)">{{ formatTagsString(row.tags) }}</span>
                </template>
            </AppNullableCell>
        </template>
        <template #td-is_auto_price_update_enabled="{row}">
            <span>{{ row.is_auto_price_update_enabled ? $t('labels.yes') : $t('labels.no') }}</span>
        </template>
        <template #td-is_auto_balance_update_enabled="{row}">
            <span>{{ row.is_auto_balance_update_enabled ? $t('labels.yes') : $t('labels.no') }}</span>
        </template>
    </Table>
    <EditProductModal ref="EditProductModal" :integration="integration" @product-edited="refreshTable"/>
    <CopyProductsModal ref="CopyProductsModal" :integration="integration" @product-copied="refreshTable"/>
    <CopyProductsContentFromIntegrationModal ref="CopyProductsContentFromIntegrationModal" :integration="integration" @product-copied="refreshTable"/>
    <RewriteInternalProductsModal ref="RewriteInternalProductsModal" :integration="integration" />
    <DisconnectIntegrationProductsModal ref="DisconnectIntegrationProductsModal" @products:detached="refreshTable"/>
    <ProductsFiltersModal ref="ProductsFiltersModal" @update:filters="refreshTable" :integration="integration"/>
    <UpdateProductContentModal ref="UpdateProductContentModal" @update:table="refreshTable" />
    <TranslateProductsContentModal ref="TranslateProductsContentModal" :integration="integration"/>
    <GroupProductModal ref="GroupProductModal" @products:grouped="refreshTable"/>
    <RunAiProcessModal ref="RunAiProcessModal" :integration="integration" @update:table="refreshTable"/>
    <ConnectPublicationsModal ref="ConnectPublicationsModal" @closed="refreshTable" />
</template>
<script>
import Table from "../../../../../../components/new/table/FetchTable.vue";
import { createTableHandle } from "../../../../../../components/new/table/tableHandle";
import Button from "../../../../../../components/new/button/AppButton.vue";
import DropdownItem from "../../../../../components/btnDropdown/DropdownItem.vue";
import EditProductModal from "../../../modals/EditProductModal.vue";
import AppNullableCell from "../../../../../components/tables/AppNullableCell.vue";
import CopyProductsModal from "../../../modals/CopyProductsModal.vue";
import CopyProductsContentFromIntegrationModal from "../../../modals/CopyProductsContentFromIntegrationModal.vue";
import RewriteInternalProductsModal from "../modals/RewriteInternalProductsModal.vue";
import DisconnectIntegrationProductsModal from "../modals/DisconnectIntegrationProductsModal.vue";
import ConnectPublicationsModal from "../../../modals/ConnectPublicationsModal.vue";
import ProductsFiltersModal from "./ProductsFiltersModal.vue";
import UpdateProductContentModal from "../modals/UpdateProductContentModal.vue";
import TranslateProductsContentModal from "../modals/TranslateProductsContentModal.vue";
import RunAiProcessModal from "../modals/RunAiProcessModal.vue";
import GroupProductModal from "../modals/GroupProductModal.vue";
import ImageZoomComponent from "../../../../../../components/new/image/ImageZoomComponent.vue";

import Price from "../../../../../../services/Price/Price";
import { PLACEHOLDER_IMAGE_URL } from "../../../../../../constants";
import { httpClient } from "../../../../../../services/HttpClient";
import { IntegrationServiceEnum } from "../../../../../enums/IntegrationServiceEnum";
import { chunk } from "../../../../../../utils";
import DateFormatter from "../../../../../../services/DateFormatter";
import MessageNotificator from "../../../../../../services/MessageNotificator";

export default {
    name: "ProductsTable",
    components: {
        Table, Button, DropdownItem, EditProductModal, AppNullableCell,
        CopyProductsModal, CopyProductsContentFromIntegrationModal, DisconnectIntegrationProductsModal,
        ProductsFiltersModal, UpdateProductContentModal, TranslateProductsContentModal,
        RunAiProcessModal, GroupProductModal, ImageZoomComponent, ConnectPublicationsModal,
        RewriteInternalProductsModal
    },
    props: {
        integration: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            PLACEHOLDER_IMAGE_URL,
            Price,
            DateFormatter,
            tableHandle: createTableHandle(),
            table: {
                url: `/integrations/${this.$route.params.integrationUuid}/products`,
                orders: [{
                    column: 'id',
                    dir: 'desc'
                }],
                columns: [
                    {key: "created_at", name: this.$t('table.titles.created_at'), width: "150px", type: "string", order: true, isShowed: true},
                    {key: "id", name: this.$t('table.titles.id'), width: "100px", type: "string", order: true, isShowed: true},
                    {key: "product_item_id", name: this.$t('table.titles.internal_id'), width: "100px", type: "string", order: true , isShowed: true}, // 1
                    {key: "source_id", name: this.$t('table.titles.source_id'), width: "100px", type: "string", order: true, isShowed: false},
                    {key: "second_source_id", name: this.$t('table.titles.second_source_id'), width: "100px", type: "string", order: true, isShowed: false},
                    {key: "integration_category_id", name: this.$t('table.titles.category'), width: "150px", type: "string", order: true, isShowed: true}, // 2
                    {key: "tags", name: this.$t('table.titles.tags'), width: "200px", type: "string", order: false, isShowed: false},
                    {key: "image", name: this.$t('table.titles.image'), width: "78px", type: "string", order: false, isShowed: true},
                    {key: "sku", name: this.$t('table.titles.sku'), width: "150px", type: "string", order: true, isShowed: true},
                    ...(this.integration.language_aliases || []).map((lang) => ({
                        key: `name_${lang}`,
                        name: this.$t(`table.titles.name`) + ` (${this.$t(`shortLang.${lang}`)})`,
                        width: "300px",
                        type: "string",
                        order: true,
                        isShowed: lang === this.integration.language_aliases[0],
                        orderColumn: `names->${lang}`
                    })),
                    ...(this.integration.language_aliases || []).map((lang) => ({
                        key: `description_${lang}`,
                        name: this.$t(`table.titles.description`) + ` (${this.$t(`shortLang.${lang}`)})`,
                        width: "300px",
                        type: "string",
                        order: true,
                        isShowed: false,
                        orderColumn: `descriptions->${lang}`
                    })),
                    ...(this.integration.language_aliases || []).map((lang) => ({
                        key: `seo_${lang}`,
                        name: this.$t(`table.titles.key_words`) + ` (${this.$t(`shortLang.${lang}`)})`,
                        width: "300px",
                        type: "string",
                        order: true,
                        isShowed: false,
                        orderColumn: `seo->${lang}`
                    })),
                    {key: "group_key", name: this.$t('table.titles.group_key'), width: "150px", type: "string", order: true, isShowed: false},
                    {key: "price", name: this.$t('table.titles.price'), width: "120px", type: "number", order: true, isShowed: true},
                    {key: "sale_price", name: this.$t('table.titles.sale_price'), width: "150px", type: "number", order: true, isShowed: false},
                    {key: "promo_price", name: this.$t('table.titles.promo_price'), width: "150px", type: "number", order: true, isShowed: false},
                    {key: "balance", name: this.$t('table.titles.balance'), width: "120px", type: "number", order: true, isShowed: true},
                    {key: "export_enabled", name: this.$t('table.titles.export_enabled'), width: "150px", type: "string", order: true, isShowed: true},
                    {key: "manufacturer_name", name: this.$t('table.titles.manufacturer'), width: "150px", type: "string", order: true, isShowed: false}, // 3
                    {key: "measurement", name: this.$t('table.titles.measurement'), width: "100px", type: "string", order: true, isShowed: false},
                    {key: "updated_at", name: this.$t('table.titles.updated_at'), width: "150px", type: "string", order: true, isShowed: false},
                    {key: "is_auto_price_update_enabled", name: this.$t('table.titles.auto_price_update_enabled'), width: "150px", type: "string", order: true, isShowed: false},
                    {key: "is_auto_balance_update_enabled", name: this.$t('table.titles.auto_balance_update_enabled'), width: "150px", type: "string", order: true, isShowed: false}
                ]
            },
            selectedUuids: [],
            nameSearchType: "contains",
            skuSearchType: "contains",
            internalIdSearchType: "all",
            name: "",
            sourceIdSearchType: "all",
            secondSourceIdSearchType: "all",
            activeFiltersCount: 0,
        }
    },
    methods: {
        clearFilters() {
            this.$refs.ProductsFiltersModal.clear();
        },
        extractLanguageAliasFromString(str) {
            const regex = /_(\w{2})$/;
            const match = str.match(regex);
            return match ? match[1] : null;
        },
        createFilterRequest() {
            let filterRequest = {
                extra_fields: ['tags']
            };

            const selectedFilters = this.$refs.ProductsFiltersModal.fields.filter(field => field.selectedAction);

            // Prepare arrays for names / descriptions / seos
            const names_match = [];
            const names_exists = [];
            const names_not_exists = [];
            const names_are_equality = [];

            const descriptions_match = [];
            const descriptions_exists = [];
            const descriptions_not_exists = [];
            const descriptions_are_equality = [];

            const seos_match = [];
            const seos_exists = [];
            const seos_not_exists = [];
            const seos_are_equality = [];

            for (let field of selectedFilters) {
                const action = field.selectedAction.key;

                // Handle localized text fields (name_*, description_*, seo_*)
                if (field.key.startsWith('name_') || field.key.startsWith('description_') || field.key.startsWith('seo_')) {
                    const lang = this.extractLanguageAliasFromString(field.key);

                    if (field.key.startsWith('name_')) {
                        if (action === 'present') {
                            names_exists.push(lang);
                        } else if (action === 'missing') {
                            names_not_exists.push(lang);
                        } else if (action === 'coincidence') {
                            names_match.push({
                                language_alias: lang,
                                type: field.value?.param?.key,
                                value: field.value?.string ?? null
                            });
                        } else if (action === 'conformity') {
                            if (field.value?.param?.key) {
                                names_are_equality.push({
                                    language_alias_from: lang,
                                    language_alias_to: field.value.param.key
                                });
                            }
                        }

                        continue;
                    }

                    if (field.key.startsWith('description_')) {
                        if (action === 'present') {
                            descriptions_exists.push(lang);
                        } else if (action === 'missing') {
                            descriptions_not_exists.push(lang);
                        } else if (action === 'coincidence') {
                            descriptions_match.push({
                                language_alias: lang,
                                type: field.value?.param?.key,
                                value: field.value?.string ?? null
                            });
                        } else if (action === 'conformity') {
                            if (field.value?.param?.key) {
                                descriptions_are_equality.push({
                                    language_alias_from: lang,
                                    language_alias_to: field.value.param.key
                                });
                            }
                        }

                        continue;
                    }

                    if (field.key.startsWith('seo_')) {
                        if (action === 'present') {
                            seos_exists.push(lang);
                        } else if (action === 'missing') {
                            seos_not_exists.push(lang);
                        } else if (action === 'coincidence') {
                            seos_match.push({
                                language_alias: lang,
                                type: field.value?.param?.key,
                                value: field.value?.string ?? null
                            });
                        } else if (action === 'conformity') {
                            if (field.value?.param?.key) {
                                seos_are_equality.push({
                                    language_alias_from: lang,
                                    language_alias_to: field.value.param.key
                                });
                            }
                        }

                        continue;
                    }
                }

                // Non-localized handling
                if (action === 'present') {
                    if (field.key === 'tag') {
                        filterRequest['exists_tag_uuids'] = field.value !== null ? field.value.map(tag => tag.uuid) : [];
                        continue;
                    }

                    if (field.key === 'balance') {
                        filterRequest = { ...filterRequest, ...field.value };
                        continue;
                    }

                    if (field.key === 'manufacturer') {
                        filterRequest['manufacturer_is_filled'] = 1;
                        continue;
                    }

                    filterRequest[field.key] = 1;
                } else if (action === 'missing') {
                    if (field.key === 'tag') {
                        filterRequest['not_exists_tag_uuids'] = field.value !== null ? field.value.map(tag => tag.uuid) : [];
                        continue;
                    }

                    if (field.key === 'balance') {
                        filterRequest = { ...filterRequest, ...field.value };
                        continue;
                    }

                    if (field.key === 'manufacturer') {
                        filterRequest['manufacturer_is_filled'] = 0;
                        continue;
                    }

                    filterRequest[field.key] = 0;
                } else if (action === 'correlate') {
                    if (field.key === 'export_enabled') {
                        filterRequest[field.key] = field.value !== null ? Number(field.value) : 0;
                    } else if (field.key === 'category_uuids') {
                        filterRequest[field.key] = field.value !== null ? field.value.map(category => category.uuid) : [];
                    } else if (field.key === 'is_auto_price_update_enabled') {
                        filterRequest[field.key] = field.value !== null ? Number(field.value) : 0;
                    } else if (field.key === 'is_auto_balance_update_enabled') {
                        filterRequest[field.key] = field.value !== null ? Number(field.value) : 0;
                    }
                } else if (action === 'coincidence') {
                    // sku uses coincidence as before
                    if (field.key === 'sku') {
                        filterRequest[`${field.key}_search_type`] = field.value?.param?.key;
                        filterRequest[`${field.key}_not_trimmed`] = field.value?.string;
                    } else if (field.key === 'manufacturer') {
                        filterRequest['manufacturer_search_type'] = field.value?.param?.key;
                        filterRequest['manufacturer_value'] = field.value?.string;
                    }
                } else if (field.key === 'created_at') {
                    filterRequest[`${field.key}`] = field.value;
                } else if (field.key === 'balance') {
                    if (field.value.balance_from) {
                        filterRequest['balance_from'] = field.value.balance_from;
                    }
                    if (field.value.balance_to) {
                        filterRequest['balance_to'] = field.value.balance_to;
                    }
                } else if (['price', 'sale_price', 'promo_price'].includes(field.key)) {
                    if (![null, undefined, ''].includes(field.value?.from)) {
                        filterRequest[`${field.key}_from`] = field.value.from;
                    }
                    if (![null, undefined, ''].includes(field.value?.to)) {
                        filterRequest[`${field.key}_to`] = field.value.to;
                    }
                }
            }

            // Attach localized arrays if present
            if (names_match.length) filterRequest['names_match'] = names_match;
            if (names_exists.length) filterRequest['names_exists'] = names_exists;
            if (names_not_exists.length) filterRequest['names_not_exists'] = names_not_exists;
            if (names_are_equality.length) filterRequest['names_are_equality'] = names_are_equality;

            if (descriptions_match.length) filterRequest['descriptions_match'] = descriptions_match;
            if (descriptions_exists.length) filterRequest['descriptions_exists'] = descriptions_exists;
            if (descriptions_not_exists.length) filterRequest['descriptions_not_exists'] = descriptions_not_exists;
            if (descriptions_are_equality.length) filterRequest['descriptions_are_equality'] = descriptions_are_equality;

            if (seos_match.length) filterRequest['seo_match'] = seos_match;
            if (seos_exists.length) filterRequest['seo_exists'] = seos_exists;
            if (seos_not_exists.length) filterRequest['seo_not_exists'] = seos_not_exists;
            if (seos_are_equality.length) filterRequest['seo_are_equality'] = seos_are_equality;

            return filterRequest;
        },
        getAvailableTransitions(objectWithTransitions) {
            return objectWithTransitions[this.mainIntegrationLanguages] ?? Object.values(objectWithTransitions)[0];
        },
        formatTagsString(tags) {
            if (!tags || tags.length === 0) {
                return '';
            }

            return tags
                .map(tag => tag.name)
                .sort((a, b) => a.localeCompare(b))
                .join(', ');
        },
        createFilterRequestWithSearch() {
            let filterRequest = this.createFilterRequest();

            if (this.tableHandle.search) {
                filterRequest.search = this.tableHandle.search;
            }

            return filterRequest;
        },
        connectProducts() {
            this.$refs.ConnectPublicationsModal.show(
                this.selectedUuids,
                this.createFilterRequestWithSearch(),
                this.tableHandle.isAllSelected,
                this.tableHandle.total
            );
        },
        disconnectProducts() {
            this.$refs.DisconnectIntegrationProductsModal.show(
                this.selectedUuids,
                this.createFilterRequest(),
                this.tableHandle.isAllSelected,
                this.tableHandle.total
            );
        },
        editProducts() {
            this.$refs.EditProductModal.show(
                this.selectedUuids,
                this.createFilterRequestWithSearch(),
                this.tableHandle.isAllSelected,
                this.tableHandle.total
            );
        },
        rewriteInternalProducts() {
            this.$refs.RewriteInternalProductsModal.show(
                this.selectedUuids,
                this.createFilterRequestWithSearch(),
                this.tableHandle.isAllSelected,
                this.tableHandle.total
            );
        },
        generateProductsContent() {
            this.$refs.RunAiProcessModal.show(
                this.selectedUuids,
                this.createFilterRequestWithSearch(),
                this.tableHandle.isAllSelected,
                this.tableHandle.total
            );
        },
        copyProductsContentFromIntegration() {
            this.$refs.CopyProductsContentFromIntegrationModal.show(
                this.selectedUuids,
                this.createFilterRequestWithSearch(),
                this.tableHandle.isAllSelected,
                this.tableHandle.total
            );
        },
        refreshTable() {
            this.activeFiltersCount = this.$refs.ProductsFiltersModal
                ? this.$refs.ProductsFiltersModal.fields.filter(f => f.selectedAction).length
                : 0;

            let requestBody = this.createFilterRequest();

            if (this.internalIdSearchType === 'connected') {
                requestBody.has_product = 1;
            }

            if (this.internalIdSearchType === 'not_connected') {
                requestBody.has_product = 0;
            }

            if (this.sourceIdSearchType === 'filled') {
                requestBody.source_id_is_filled = 1;
            }

            if (this.secondSourceIdSearchType === 'filled') {
                requestBody.second_source_id_is_filled = 1;
            }

            if (this.sourceIdSearchType === 'not_filled') {
                requestBody.source_id_is_filled = 0;
            }

            if (this.secondSourceIdSearchType === 'not_filled') {
                requestBody.second_source_id_is_filled = 0;
            }

            this.tableHandle.fetchRows(requestBody);
        },
        async removeProductItems() {
            new MessageNotificator().confirm(this.$t('alerts.confirm_delete', {
                value: this.$t('labels.products')
            }), async () => {
                let request = [];

                this.selectedUuids.forEach((uuid) => {
                    request.push({
                        method: 'DELETE',
                        path: `/integrations/products/${uuid}`
                    })
                })

                await Promise.all(chunk(request, 10).map((requestChunk) => {
                    return httpClient.post('/batch', {
                        data: requestChunk
                    })
                }))

                this.refreshTable()
            })
        }
    },
    computed: {
        isSupplier() {
            return this.integration.service === IntegrationServiceEnum.SUPPLIER
        },
        mainIntegrationLanguages() {
            return this.integration.language_aliases[0];
        },
    },
    mounted() {
        this.refreshTable()
    }
}
</script>
<style scoped>
.long-string {
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    line-clamp: 2;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
}
.product-cell {
    max-width: 500px;
    width: 100%;
}
.text-container {
    min-width: 0;
    overflow: hidden;
    flex: 1;
}
.text-smaller {
    font-size: 12px;
    line-height: 18px;
}
</style>
